import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/index';
import { setColor } from '../store/colorSlice';
import { setLogo } from '../store/logoSlice';
import { apiGetMineInfo, apiGetPage, apiGetCustom, apiGetApplicationDetail } from '../request/api'
import { imageUrl, zeusApp } from '../request/index'
import { LogoutOutlined } from '@ant-design/icons';
import '../assets/css/header.css'

interface OrderItem {
    name: string;
    url: string;
}

interface ApiPageRow {
    mainLand: string;
    siteName: string;
    siteLanguage: string; // 格式: "简体中文:zh,English:en"
    siteCode: string;
}

interface TransformedLanguageList {
    areaName: string;
    items: {
        name: string;
        siteCode: string;
        languageList: { label: string; value: string }[];
        isClose: boolean;
    }[];
}

const Header: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const logoImage = localStorage.getItem('logoImage')
    const themeColor = useSelector((state: RootState) => state.themeColor.value);
    const [isLanguageVisible, setIsLanguageVisible] = useState<boolean>(false);
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);
    const dispatch = useDispatch();
    const [hoveredIndex, setHoveredIndex] = useState<{ regionIndex: number | null, countryIndex: number | null }>({
        regionIndex: null,
        countryIndex: null
    });
    const [transformedList, setTransformedList] = useState<TransformedLanguageList[]>([]);
    const [loginState, setLoginState] = useState(0)
    const [isLanguageHover, setIsLanguageHover] = useState<boolean>(false);
    const [isUserHover, setIsUserHover] = useState<boolean>(false);
    const [isUserIconHovered, setIsUserIconHovered] = useState(false);
    const [navList, setNavList] = useState<OrderItem[]>([])
    const intervalRef = React.useRef<NodeJS.Timeout | null>(null);
    const [selectedLanguageDisplay, setSelectedLanguageDisplay] = useState<string>('**-**');
    const languageCode = localStorage.getItem('siteLanguage') || 'zh';
    const [hoveredLanguageItem, setHoveredLanguageItem] = useState<{
        regionIndex: number | null;
        countryIndex: number | null;
        itemIndex: number | null;
    }>({
        regionIndex: null,
        countryIndex: null,
        itemIndex: null
    });

    useEffect(() => {
        fetchUserInfo();
        apiGetApplicationDetail('APPLICATION_LOGO').then(res => {
            localStorage.setItem('logoImage', res.data.data)
        })
        apiGetPage({
            tid: '0A2EDC7623D14736828579C3BA07A038',
            search: '',
            page: 1,
            size: 100,
            sort: '',
        }).then(res => {
            const rows: ApiPageRow[] = res.data.data.rows;
            const grouped: { [key: string]: { name: string; languageList: { label: string; value: string }[] }[] } = {};

            rows.forEach(row => {
                const languageList = row.siteLanguage.split(',').map(item => {
                    const [label, value] = item.split(':');
                    return { label, value };
                });

                if (!grouped[row.mainLand]) {
                    grouped[row.mainLand] = [];
                }

                grouped[row.mainLand].push({
                    name: row.siteName,
                    languageList,
                });
            });

            // 回填已选择的语言
            const storedLang = localStorage.getItem('siteLanguage') || 'zh';
            const storedSiteCode = localStorage.getItem('siteCode') || 'SG';
            setSelectedLanguageDisplay(`${storedSiteCode}-${storedLang}`);

            // 转换成渲染数据结构
            const transformed: TransformedLanguageList[] = Object.entries(grouped).map(([areaName, items]) => ({
                areaName,
                items: items.map((item) => ({
                    name: item.name,
                    siteCode: rows.find(row => row.siteName === item.name)?.siteCode || 'UNKNOWN',
                    languageList: item.languageList,
                    isClose: true
                }))
            }));

            setTransformedList(transformed);
        });

        // 获取导航栏数据
        apiGetCustom({
            pageId: '310ED453D3B244909CBD0E8EFE4BF0FA',
            languageCode: languageCode
        }).then(res => {
            const order = ['index', 'service', 'news', 'about', 'contact'];

            const sortedNavList = res.data.data
                .filter((item: { code: string; }) =>
                    item.code !== 'pageURL' &&
                    item.code !== 'pageTitle'
                )
                .sort((a: { code: string; }, b: { code: string; }) => {
                    const aIndex = order.indexOf(a.code);
                    const bIndex = order.indexOf(b.code);
                    if (aIndex >= 0 && bIndex >= 0) return aIndex - bIndex;
                    if (aIndex >= 0) return -1;
                    if (bIndex >= 0) return 1;
                    return 0;
                })
                .map((item: { name: any; value: any; }) => ({
                    name: item.value,
                    url: item.name
                }));

            setNavList(sortedNavList);
        });

        intervalRef.current = setInterval(() => {
            fetchUserInfo();
        }, 60000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [dispatch]);

    const fetchUserInfo = () => {
        apiGetMineInfo().then(res => {
            if (res.data.data !== null) {
                setLoginState(1);
            } else {
                setLoginState(0);
            }
        }).catch(() => {
            setLoginState(0);
        });
    };

    const handleMouseEnter = (index: number) => setHoverIndex(index);
    const handleMouseLeave = () => setHoverIndex(null);
    const handleNavigation = (path: string) => navigate(path, { state: { resetView: true } });

    const toMine = () => {
        if (loginState) navigate('/mine');
        else navigate('/login');
    }

    const showLanguage = () => setIsLanguageVisible(!isLanguageVisible);

    return (
        <div className="ezheader">
            <div className='ezheader-inner'>
                <div className='ezheader-padding'>
                    <div className='header_image' onClick={() => { navigate('/index'); setIsLanguageVisible(false) }}>
                        <span></span>
                        <img src={`${imageUrl}${logoImage}?zeus-app=${zeusApp}`} />
                    </div>
                    <div className='header_nav'>
                        {navList.map((item, index) => (
                            <div className="nav_item"
                                key={index}
                                // onClick={() => { setIsLanguageVisible(false); handleNavigation(item.url) }}
                                onMouseEnter={() => handleMouseEnter(index)}
                                onMouseLeave={handleMouseLeave}
                                style={{
                                    color: location.pathname === item.url || hoverIndex === index ? themeColor : 'inherit',
                                    cursor: 'pointer',
                                }}
                            >
                                {/* {item.name} */}
                            </div>
                        ))}
                    </div>
                    <div className='header_right'>
                        {/* 语言图标 */}
                        <div className='header_language' onClick={showLanguage}>
                            <i className="iconfont icon-international"
                                onMouseEnter={() => setIsLanguageHover(true)}
                                onMouseLeave={() => setIsLanguageHover(false)}
                                style={{ color: isLanguageVisible || isLanguageHover ? themeColor : 'inherit', fontSize: '22px' }}
                            />
                        </div>
                        {/* 语言文字 */}
                        <div
                            className='header_language_txt'
                            onMouseEnter={() => setIsLanguageHover(true)}
                            onMouseLeave={() => setIsLanguageHover(false)}
                            onClick={showLanguage}
                            style={{ color: isLanguageVisible || isLanguageHover ? themeColor : 'inherit' }}
                        >
                            {selectedLanguageDisplay}
                        </div>

                        {/* 用户图标 & 退出 */}
                        <div className="header_language_right"
                            style={{
                                fontSize: "22px",
                                color: isUserIconHovered ? themeColor : 'inherit',
                                cursor: 'pointer'
                            }}>
                            <i
                                className="iconfont icon-User"
                                onClick={() => { setIsLanguageVisible(false); }}
                                style={{ fontSize: '22px' }}
                                onMouseEnter={() => { setIsUserIconHovered(true); setIsUserHover(false); }}
                                onMouseLeave={() => { if (!isUserHover) setIsUserIconHovered(false); }}
                            />
                            {/* {loginState == 1 &&
                                <div className="loginOut"
                                    onMouseEnter={() => { setIsUserHover(true); setIsUserIconHovered(true); }}
                                    onMouseLeave={() => { setIsUserHover(false); setIsUserIconHovered(false); }}
                                    style={{
                                        color: isUserHover ? themeColor : '#000',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        height: 0,
                                        width:0,
                                        gap: '5px',
                                        fontSize: '14px',
                                        opacity: isUserIconHovered ? 1 : 0,
                                        visibility: isUserIconHovered ? 'visible' : 'hidden',
                                        transform: isUserIconHovered ? 'translateY(0)' : 'translateY(-10px)'
                                    }}
                                    onClick={() => { handleNavigation("/login") }}
                                    >
                                    <LogoutOutlined />
                                    <span>退出登录</span>
                                </div>
                            } */}
                        </div>
                    </div>
                </div>

                {/* 语言弹层 */}
                {isLanguageVisible &&
                    <div className='language'>
                        <div className='contury_body_item'>
                            {transformedList.map((area, regionIndex) => (
                                <div key={regionIndex} className="language_item">
                                    <div className='language_header'>{area.areaName}</div>
                                    {area.items.map((country, countryIndex) => (
                                        <div
                                            className='contury_item'
                                            key={countryIndex}
                                            onMouseEnter={() => setHoveredIndex({ regionIndex, countryIndex })}
                                            onMouseLeave={() => setHoveredIndex({ regionIndex: null, countryIndex: null })}
                                            style={{
                                                backgroundColor:
                                                    hoveredIndex.regionIndex === regionIndex && hoveredIndex.countryIndex === countryIndex
                                                        ? '#f6f6f8'
                                                        : '#ffffff',
                                                transition: 'background-color 0.3s ease'
                                            }}
                                        >
                                            <div className='contury_name'>{country.name}</div>
                                            <div className='contury_body_item'>
                                                {country.languageList.map((lang, langIndex) => (
                                                    <div
                                                        key={langIndex}
                                                        onMouseEnter={() =>
                                                            setHoveredLanguageItem({ regionIndex, countryIndex, itemIndex: langIndex })
                                                        }
                                                        onMouseLeave={() =>
                                                            setHoveredLanguageItem({ regionIndex: null, countryIndex: null, itemIndex: null })
                                                        }
                                                        onClick={() => {
                                                            const selectedCountry = area.items[countryIndex];
                                                            const selectedLang = selectedCountry.languageList[langIndex];
                                                            localStorage.setItem('siteLanguage', selectedLang.value);
                                                            localStorage.setItem('siteCode', selectedCountry.siteCode);
                                                            setIsLanguageVisible(false);
                                                            window.location.reload();
                                                        }}
                                                        style={{
                                                            color:
                                                                hoveredLanguageItem.regionIndex === regionIndex &&
                                                                    hoveredLanguageItem.countryIndex === countryIndex &&
                                                                    hoveredLanguageItem.itemIndex === langIndex
                                                                    ? themeColor
                                                                    : '#33364b',
                                                            cursor: 'pointer',
                                                            transition: 'color 0.3s ease',
                                                            padding: '4px 16px 0 0',
                                                        }}
                                                    >
                                                        {lang.label}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                }
            </div>

            {/* 遮罩 */}
            {isLanguageVisible &&
                <div className="language_overlay" onClick={() => { setIsLanguageVisible(false) }}></div>
            }
        </div>
    );
};

export default Header;
